import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'

// Material-UI imports
import { Box, Button, Menu, MuiThemeProvider, Paper, Typography, MenuItem } from '@material-ui/core'
import { css } from '@emotion/css'
import { ColorBaseSkyBlue } from 'assets/styles/variables'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import { useMinutesTemplatesStyles } from './minutes-templates-styles'

// Theme and styles
import { materialAtlasTheme } from 'material-atlas/theme'

// Utilities
import MomentUtils from '@date-io/moment'
import { addKeyPressHandler } from 'common/util/functions'

// Business layer
import authenticationManager from 'businesslayer/authenticationManager'
import { useMinutesManager } from 'pages/minutes-manager-page/use-minutes-manager'
import { useMinutesTemplates } from 'businesslayer/networkrequests/templates/useMinutesTemplates'
import { getLocalStorageItem, getSessionStorageItem } from 'businesslayer/minutesLocalStore'
import { transientStorageManager } from 'businesslayer/minutesSessionStore'
import {
    getNotificationSettings,
    postNotificationSettings,
    patchNotificationSettings
} from 'businesslayer/networkrequests/notificationsSettings'
import { useMinutesPolling } from 'common/customHooks/pollingApiStatus'
import { POLLING_INTERVAL } from 'common/constants/constants'

// Components
import {
    PageLayout,
    PageHeader,
    MinutesManagerToolbar,
    PageBodyTemplate
} from 'components/layout/page-layout'
import { AtlasButton } from 'components/atlas-material/button'
import LinearIndeterminate from 'components/shared/LinearProgress'
import TemplateDrawer from './TemplateDrawer'
import MinutesTemplatesComponent from 'components/minutes-templates/minutesTemplatesComponent'
import PreviewTemplateDrawer from './preview-template-drawer'
import StatusToast from 'components/atlas-components/atlas-toast/Toast'

// Icons
import { AtlasIcon } from 'web-components/atlas-icon'
import PlusIcon from 'assets/icons/PlusIcon'
import NoActionIcon from 'assets/icons/NoActionIcon'
import DiligentBrandIcon from 'assets/icons/DiligentBrandIcon'
import DownloadIcon from 'assets/icons/DownloadIcon'

// Internationalization
import i18n from 'common/util/internationalization'
import { TEMPLATE_UPLOAD_STATUS } from '../../../src/components/minutetaker/components/InvitationView/types'

// Network requests
import {
    downloadTemplate,
    fetchPreviewTemplatePDF,
    updateSitePreferredTemplate
} from 'businesslayer/networkrequests/templateService'
import PopoverButton from 'components/shared/PopoverButton'
import DefaultStarIcon from 'assets/icons/DefaultStarIcon'
import {
    MAX_TEMPLATE_COMPANY_NAME_LENGTH,
    MINUTES_TEMPLATES_DILIGEN_ACADEMY_TEMPLATE_DESCRIPTION,
    MINUTES_TEMPLATES_DILIGENT_ACADEMY_TEMPLATE_TITLE,
    MINUTES_TEMPLATES_DILIGENT_TEMPLATE_DESCRIPTION,
    MINUTES_TEMPLATES_DILIGENT_TEMPLATE_TITLE,
    TEMPLATE_OVERLAY_OPTIONS
} from 'common/constants/constants'

import {
    getTemplateServiceToken,
    startTokenRenewal
} from 'businesslayer/networkrequests/getTemplateServiceToken'
import ssoConfig from '../../config/ssoConfig.json'
import DownLoadIconBlack from 'assets/icons/DowloadBlackIcon'
import { AtlasTooltip } from 'components/atlas-components/atlas-tooltip/Tooltip'
import { ToastNotification } from 'components/atlas-components/atlas-toast/ToastNotification'
import { NewDialogTextField } from 'components/shared/StyledComponents'
import TextLengthCounter from 'components/shared/TextLengthCounter'
import containsHTML from 'components/minutetaker/components/Utils/Utils'

/**
 * Minutes Templates Page
 */

declare global {
    interface Window {
        canAddMinutes: boolean
    }
}

export const MinutesTemplatesPage = () => {
    const {
        templateLists,
        setTemplateLists,
        isLoading,
        fetchMinutesTemplates,
        refetchAndReplaceLastPage,
        refreshTemplateList,
        totalTemplates,
        setTotalTemplates,
        systemTemplate
    } = useMinutesTemplates()
    const { canAddMinutes } = useMinutesManager()
    const {
        toasts,
        isPolling,
        setToasts,
        fetchMinutesDocuments: fetchPollingMinutesDocuments
    } = useMinutesPolling()

    const [isPreviewDrawerOpen, setIsPreviewDrawerOpen] = useState(false)
    const [page, setPage] = useState<number>(1)
    const [sortBy, setSortBy] = useState<string>('updatedAt')
    const [order, setOrder] = useState<string>('desc')
    const [imageUrls, setImageUrls] = useState<string[]>([])
    const [perPage] = useState<number>(20)
    const [loading, setLoading] = useState(false)
    const [defaultTemplateId, setDefaultTemplateId] = useState<any>()
    const [currentPreviewedTemplate, setCurrentPreviewedTemplate] = useState<any>(null)
    const [showToast, setShowToast] = useState(false)
    const [toastMessage, setToastMessage] = useState('')
    const [toastType, setToastType] = useState('')
    const [lastPageLoaded, setLastPageLoaded] = useState<number>(1)
    const deletedTemplateIds = useRef<Set<string>>(new Set())
    const [templateAdded, setTemplateAdded] = useState<boolean>(false)
    const [templateListBeforeAdd, setTemplateListBeforeAdd] = useState<any[]>([])
    const [paginationStateBeforeAdd, setPaginationStateBeforeAdd] = useState<{
        page: number
        lastPageLoaded: number
    }>({ page: 1, lastPageLoaded: 1 })
    const [companyName, setCompanyName] = useState<string>(
        transientStorageManager.companyName || ''
    )
    const [isSavingCompany, setIsSavingCompany] = useState<boolean>(false)

    const removeToast = useCallback(
        (toastId: number | string) => {
            setToasts((prev) => prev.filter((t) => t.id !== toastId))
        },
        [setToasts]
    )

    interface FileItem {
        name: string
        size: number
        file: File | null
    }

    const [isTemplateDrawerOpen, setIsTemplateDrawerOpen] = useState(false)
    const [isEditTemplateDrawerOpen, setIsEditTemplateDrawerOpen] = useState(false)
    const [uploadStatus, setUploadStatus] = useState<TEMPLATE_UPLOAD_STATUS | null>(
        TEMPLATE_UPLOAD_STATUS.DEFAULT
    )
    const [items, setItems] = useState<FileItem | null>(null)
    let fileUploadError = ''
    const handleAddTemplate = useCallback(() => {
        setIsTemplateDrawerOpen(true)
    }, [])
    const classes = useMinutesTemplatesStyles({
        numColumns: canAddMinutes ? 3 : 2
    })

    // Initialize token auto-renewal system for template operations
    useEffect(() => {
        const { _env_ } = window
        const SSO_AUTHORITY_URL =
            process.env.NODE_ENV !== 'development' ? _env_.SSO_AUTHORITY_URL : ssoConfig.authority
        const isBoardLogin = () => {
            const platform = getSessionStorageItem('parentPlatform')
            return platform === 'boardswebadmin' || platform === 'boardswebdirector'
        }
        // Get current access token from localStorage
        const currentAccessToken = getLocalStorageItem('sessionId')
        if (isBoardLogin() || process.env.NODE_ENV === 'development') {
            if (currentAccessToken) {
                // Create params for token renewal
                const params = new URLSearchParams()
                params.append('client_id', ssoConfig.minutes_clientId)
                params.append('grant_type', ssoConfig.template_doc_service_grantType)
                params.append('token', currentAccessToken)
                params.append('scope', ssoConfig.template_doc_service_scope)

                // Initialize auto-renewal for any existing template token
                getTemplateServiceToken(SSO_AUTHORITY_URL, params)
                    .then(() => {
                        // Start token renewal every 25 minutes
                        startTokenRenewal(SSO_AUTHORITY_URL, params)
                    })
                    .catch((error) => {
                        console.error(
                            'MinutesTemplatesPage.tsx: Failed to get template service token:',
                            error
                        )
                    })
            } else {
                console.error('No access token available for template service auto-renewal')
            }
        }
    }, [])

    // Show error message for company name validation
    const companyNameErrorMessage = useMemo(() => {
        const CompanyNameTrimmed = (companyName || '').trim()
        if (CompanyNameTrimmed.length === 0) {
            return i18n.t('MINUTES_TEMPLATES_COMPANY_NAME_REQUIRED') as string
        } else if (CompanyNameTrimmed.length === 1) {
            return i18n.t('MINUTES_TEMPLATES_COMPANY_NAME_MIN_LENGTH') as string
        } else if ((companyName || '').length > MAX_TEMPLATE_COMPANY_NAME_LENGTH) {
            return i18n.t('MINUTES_TEMPLATES_COMPANY_NAME_MAX_LENGTH') as string
        } else if (containsHTML(companyName || '')) {
            return i18n.t('NO_HTML_MESSAGE')
        } else if (CompanyNameTrimmed) {
            let isValidCompanyName = true
            try {
                const unicodePattern = new RegExp("^[\\p{L}\\p{M}0-9@.#$%^&* '-]+$", 'u')
                isValidCompanyName = unicodePattern.test(CompanyNameTrimmed)
            } catch (err) {
                const asciiPattern = /^[-A-Za-z0-9@.#$%^&* ']+$/
                isValidCompanyName = asciiPattern.test(CompanyNameTrimmed)
            }
            if (!isValidCompanyName) {
                return i18n.t('MINUTES_TEMPLATES_COMPANY_NAME_INVALID_CHARACTERS') as string
            }
        }
        return ''
    }, [companyName])

    // Save company name to server
    const handleSaveCompanyName = useCallback(async () => {
        const CompanyNameTrimmed = (companyName || '').trim()

        try {
            setIsSavingCompany(true)

            // Fetch existing minutes_settings
            const list = await getNotificationSettings()
            const existing = Array.isArray(list?.data) ? list.data[0] : null

            if (!existing) {
                // Create minutes_settings with platform, site_name and optional company_name
                const payload = {
                    data: {
                        attributes: {
                            platform: transientStorageManager.platform,
                            site_name: transientStorageManager.siteName,
                            company_name: CompanyNameTrimmed,
                            action_notification: true
                        }
                    }
                }
                await postNotificationSettings(JSON.stringify(payload))
            } else {
                // Update existing minutes_settings to set company_name only
                const payload = {
                    data: {
                        attributes: {
                            company_name: CompanyNameTrimmed
                        }
                    }
                }
                await patchNotificationSettings(JSON.stringify(payload), existing.id)
            }
            // Update local cache
            transientStorageManager.companyName = CompanyNameTrimmed
            setCompanyName(CompanyNameTrimmed)
        } catch (error) {
            setShowToast(true)
            setToastType('error')
            console.error(error)
        } finally {
            setIsSavingCompany(false)
        }
    }, [companyName])

    // Company name section UI
    const companySection = (
        <Paper className={classes.companyContainer} elevation={0}>
            <Box className={classes.companyHeader}>
                <Typography className={classes.companyLabel}>
                    {i18n.t('MINUTES_TEMPLATES_COMPANY_NAME_LABEL')}
                </Typography>
                <Typography className={classes.companyDescription}>
                    {i18n.t('MINUTES_TEMPLATES_COMPANY_NAME_DESCRIPTION')}
                </Typography>
            </Box>
            <Box className={classes.companyRow}>
                <NewDialogTextField
                    placeholder={i18n.t('MINUTES_TEMPLATES_COMPANY_NAME_PLACEHOLDER') as string}
                    value={companyName}
                    type="text"
                    error={!!companyNameErrorMessage}
                    helperText={
                        <TextLengthCounter
                            maxValue={MAX_TEMPLATE_COMPANY_NAME_LENGTH}
                            value={companyName}
                            colour={true}
                            errorText={companyNameErrorMessage}
                        />
                    }
                    inputProps={{ maxLength: MAX_TEMPLATE_COMPANY_NAME_LENGTH }}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setCompanyName(e.target.value)
                    }
                    className={classes.companyTextField}
                    data-testid="CompanyName_TextField"
                />
                <Button
                    role="button"
                    tabIndex={0}
                    data-analytics="MM-Template-CompanyName-Save"
                    data-testid="CompanyName_Save_Btn"
                    disableFocusRipple
                    disableTouchRipple
                    disableRipple
                    className={classes.buttonContainerSave}
                    onClick={handleSaveCompanyName}
                    onKeyPress={(e) => (addKeyPressHandler(e) ? handleSaveCompanyName() : null)}
                    disabled={
                        isSavingCompany ||
                        (companyName || '').trim().length === 0 ||
                        companyNameErrorMessage !== '' ||
                        (companyName || '').trim() ===
                            (transientStorageManager.companyName || '').trim()
                    }>
                    <Typography variant="body1" aria-label={i18n.t('SAVE') as string}>
                        {i18n.t('SAVE')}
                    </Typography>
                </Button>
            </Box>
        </Paper>
    )

    // Polling for writing style status updates
    useEffect(() => {
        let intervalId: number | undefined
        if (isPolling) {
            fetchPollingMinutesDocuments()
            intervalId = setInterval(fetchPollingMinutesDocuments, POLLING_INTERVAL) as number
        }
        return () => {
            if (intervalId) {
                clearInterval(intervalId)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPolling])

    useEffect(() => {
        const onSettingsUpdated = () => {
            setCompanyName(transientStorageManager.companyName || '')
        }
        window.addEventListener('minutes:settings-updated', onSettingsUpdated)
        return () => window.removeEventListener('minutes:settings-updated', onSettingsUpdated)
    }, [])

    useEffect(() => {
        fetchMinutesTemplates(page, sortBy, order, perPage)
        setLastPageLoaded(page) // Track the current page being loaded
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, sortBy, order])

    // Separate effect to refresh templates when drawer closes
    useEffect(() => {
        if (!isTemplateDrawerOpen && templateAdded) {
            // Restore pagination and handle the new template
            restorePaginationAfterAdd()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTemplateDrawerOpen])

    useEffect(() => {
        if (templateLists?.length > 0) {
            const defaultTemplate = templateLists.find((t) => t?.isDefault)
            if (defaultTemplate) {
                setDefaultTemplateId(defaultTemplate?.id)
            } else {
                setDefaultTemplateId('default')
            }
        }
    }, [templateLists])

    useEffect(() => {
        const onSettingsUpdated = () => {
            setCompanyName(transientStorageManager.companyName || '')
        }
        window.addEventListener('minutes:settings-updated', onSettingsUpdated)
        return () => window.removeEventListener('minutes:settings-updated', onSettingsUpdated)
    }, [])

    const handlePageChange = () => {
        setPage((prev) => {
            const newPage = prev + 1
            setLastPageLoaded(newPage)
            return newPage
        })
    }

    const handleSortBy = (sortBy: string, order: string) => {
        setSortBy(sortBy)
        setPage(1)
        setOrder(order)
    }

    const handleGetSystemDefaultTemplatePreview = async (template: any) => {
        setIsPreviewDrawerOpen(true)
        setCurrentPreviewedTemplate(template)
        getSystemDefaultPreviewTemplate(template?.id)
    }

    const getSystemDefaultPreviewTemplate = async (templateId?: string) => {
        try {
            setLoading(true)
            const SystemTemplateId = templateId
            if (!SystemTemplateId) {
                setImageUrls([])
                return
            }
            const response = await fetchPreviewTemplatePDF(SystemTemplateId)
            if (response?.success && response?.images && response?.images?.length > 0) {
                setImageUrls(response?.images)
            } else {
                setImageUrls([])
                console.error(response?.error)
            }
        } catch (err) {
            setImageUrls([])
            console.error(err)
        } finally {
            setLoading(false)
        }
    }

    const reloadTemplateAfterDelete = useCallback(
        (templateId: string) => {
            // Prevent duplicate deletions
            if (deletedTemplateIds.current.has(templateId)) {
                return
            }
            deletedTemplateIds.current.add(templateId)

            // Remove the deleted template immediately from the list
            setTemplateLists((prev) => {
                const filtered = prev.filter((template) => template.id !== templateId)
                return filtered
            })

            // Update total count immediately to fix "Show More" button visibility
            setTotalTemplates((prev) => prev - 1)

            // Refetch the last page to fill the gap (only if we have loaded more than page 1)
            if (lastPageLoaded > 1) {
                setTimeout(() => {
                    refetchAndReplaceLastPage(lastPageLoaded, sortBy, order, perPage)
                    // Clear the deleted template ID after refetch to allow future deletions
                    setTimeout(() => {
                        deletedTemplateIds.current.delete(templateId)
                    }, 200)
                }, 100) // Small delay to ensure state updates are complete
            } else {
                // For page 1, refresh via hook to also update system templates/default state
                setTimeout(async () => {
                    try {
                        await refreshTemplateList(1, sortBy, order, perPage)
                    } catch (error) {
                        console.error('Error updating template list:', error)
                    }
                    // Clear the deleted template ID
                    deletedTemplateIds.current.delete(templateId)
                }, 100)
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        [
            setTemplateLists,
            setTotalTemplates,
            lastPageLoaded,
            refetchAndReplaceLastPage,
            refreshTemplateList,
            sortBy,
            order,
            perPage
        ]
    )

    const resetPaginationAndReload = useCallback(() => {
        setPage(1)
        setLastPageLoaded(1)
        fetchMinutesTemplates(1, sortBy, order, perPage)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const resetPaginationAndReloadWithCurrentSort = useCallback(() => {
        setPage(1)
        setLastPageLoaded(1)
        fetchMinutesTemplates(1, sortBy, order, perPage)
    }, [sortBy, order, perPage, fetchMinutesTemplates])

    const handleTemplateAdded = useCallback(() => {
        // Store the current template list and pagination state before adding
        setTemplateListBeforeAdd([...templateLists])
        setPaginationStateBeforeAdd({ page, lastPageLoaded })
        setTemplateAdded(true)
    }, [templateLists, page, lastPageLoaded])

    const restorePaginationAfterAdd = useCallback(async () => {
        const { page: prevPage, lastPageLoaded: prevLastPageLoaded } = paginationStateBeforeAdd

        // First, refresh page 1 to get the new template and reorder it
        await refreshTemplateList(1, sortBy, order, perPage)

        // If user was viewing multiple pages, restore the pagination
        if (prevLastPageLoaded > 1) {
            // Set the page back to what it was
            setPage(prevPage)
            setLastPageLoaded(prevLastPageLoaded)

            // Refetch all the pages that were loaded before
            for (let p = 2; p <= prevLastPageLoaded; p++) {
                await fetchMinutesTemplates(p, sortBy, order, perPage)
            }
        }

        // Reset states
        setTemplateAdded(false)
        setTemplateListBeforeAdd([])
        setPaginationStateBeforeAdd({ page: 1, lastPageLoaded: 1 })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        paginationStateBeforeAdd,
        templateListBeforeAdd,
        refreshTemplateList,
        fetchMinutesTemplates,
        sortBy,
        order,
        perPage
    ])

    const handleCardOverflowClick = async (optionId, popoverOnClose, selectedTemplate?: any) => {
        if (optionId === TEMPLATE_OVERLAY_OPTIONS.OPTION_DEFAULT) {
            try {
                if (selectedTemplate?.id) {
                    // Create object for metadata update
                    const templateData = {
                        title:
                            selectedTemplate?.site !== null
                                ? selectedTemplate?.title || ''
                                : i18n.t('MINUTES_TEMPLATES_DILIGENT_TEMPLATE_TITLE'),
                        description:
                            selectedTemplate?.site !== null
                                ? selectedTemplate?.description || ''
                                : i18n.t('MINUTES_TEMPLATES_DILIGENT_TEMPLATE_DESCRIPTION'),
                        isDefault: true
                    }

                    //Update metadata using object (not FormData)
                    await updateSitePreferredTemplate(selectedTemplate?.id, templateData)
                    resetPaginationAndReloadWithCurrentSort()
                    setDefaultTemplateId('default')
                }
                popoverOnClose(optionId)
            } catch (error) {
                console.error('Error setting default template:', error)
            }
        }
        if (optionId === TEMPLATE_OVERLAY_OPTIONS.OPTION_DOWNLOAD) {
            popoverOnClose(optionId)
            if (selectedTemplate?.id) {
                await downloadTemplateFile(selectedTemplate.id, selectedTemplate.title)
            }
        }
    }

    const downloadTemplateFile = async (templateId: string, templateTitle?: string) => {
        let result
        if (templateId && templateTitle) {
            result = await downloadTemplate(templateId, templateTitle)
        }

        // Reset toast state first to ensure new toast appears
        setShowToast(false)

        // Use setTimeout to ensure state reset before showing new toast
        setTimeout(() => {
            setShowToast(true)
            if (result?.success) {
                setToastType('success')
                setToastMessage(i18n.t('TEMPLATE_DOWNLOAD_SUCCESS'))
            } else {
                setToastType('error')
                setToastMessage(i18n.t('TEMPLATE_DOWNLOAD_FAIL'))
            }
        }, 0)
    }

    const renderToastNotification = useMemo(
        () => (
            <ToastNotification
                message={toastMessage}
                showToast={showToast}
                setShowToast={setShowToast}
                toastType={toastType}
            />
        ),
        [toastMessage, showToast, setShowToast, toastType]
    )
    const toastNotification = showToast ? renderToastNotification : null

    // Define options inside system templates for dynamic i18n
    const SYSTEM_TEMPLATE_OPTIONS: any[] = [
        {
            id: 1,
            value: 'DEFAULT',
            testid: 'MinutesCardOverflow_Default',
            dataAnalytics: 'MM-Minute-Template-Default',
            label: i18n.t('MINUTES_TEMPLATES_DEFAULT_TEMPLATE'),
            icon: <DefaultStarIcon />
        },
        {
            id: 3,
            value: 'DOWNLOAD',
            testid: 'MinutesCardOverflow_Template_Download',
            dataAnalytics: 'MM-Minute-Template-Download',
            label: i18n.t('MINUTES_TEMPLATES_DOWNLOAD_TEMPLATE'),
            icon: <DownLoadIconBlack />
        }
    ]

    return (
        <MuiThemeProvider theme={materialAtlasTheme}>
            <MuiPickersUtilsProvider utils={MomentUtils}>
                <PageLayout>
                    <PageHeader
                        title={i18n.t('PAGE_TITLE_MM')}
                        onSignout={authenticationManager.handleSignout}>
                        <MinutesManagerToolbar />
                    </PageHeader>

                    <Box className={classes.header} data-testid="MinutesTemplates_header">
                        <Box>
                            <Typography
                                variant="h1"
                                component="h1"
                                className={classes.subHeaderText}>
                                {i18n.t('MINUTES_TEMPLATES')}
                            </Typography>
                            {canAddMinutes && (
                                <Typography
                                    variant="h6"
                                    component="h6"
                                    className={classes.subTitleText}>
                                    {i18n.t('MINUTES_TEMPLATES_SUBTITLE')}
                                </Typography>
                            )}
                        </Box>
                        {canAddMinutes && (
                            <AtlasButton
                                role="button"
                                tabIndex={0}
                                data-analytics="MM-Template-Add"
                                data-testid="ManagerTemplate_Add_Template_Btn"
                                disableRipple
                                className={classes.createMinutesContainerCls}
                                onKeyPress={(e) =>
                                    addKeyPressHandler(e) ? handleAddTemplate : null
                                }
                                onClick={handleAddTemplate}>
                                <Box
                                    className={classes.createMinutesTxtCls}
                                    data-testid="MinutesTemplates_Add_template_ICON">
                                    {i18n.t('MINUTES_TEMPLATES_ADD_TEMPLATES')} <PlusIcon />
                                </Box>
                            </AtlasButton>
                        )}
                    </Box>
                    {<LinearIndeterminate loading={isLoading || isSavingCompany} />}
                    <PageBodyTemplate>
                        {/* Company name container */}
                        <Box className={classes.rootContainerCompany}>{companySection}</Box>
                        <Box>
                            <Typography
                                variant="h1"
                                component="h1"
                                className={classes.allTemplatesText}>
                                {i18n.t('MINUTES_DILIGENT_TEMPLATES')}
                            </Typography>
                        </Box>
                        {/* System templates container */}
                        <Box className={classes.rootContainer}>
                            {Array.isArray(systemTemplate) &&
                                systemTemplate.length > 0 &&
                                systemTemplate.map((sysTpl: any) => (
                                    <Paper key={sysTpl.id} className={classes.root} elevation={0}>
                                        <Box className={classes.iconBox}>
                                            <Box className={classes.logo}>
                                                <DiligentBrandIcon />
                                            </Box>
                                            <Box className={classes.textBox}>
                                                <Typography
                                                    className={classes.title}
                                                    data-analytics="MM-Template-PreviewSystemTemplate"
                                                    onClick={() =>
                                                        handleGetSystemDefaultTemplatePreview(
                                                            sysTpl
                                                        )
                                                    }>
                                                    {sysTpl.title ===
                                                    MINUTES_TEMPLATES_DILIGENT_TEMPLATE_TITLE
                                                        ? i18n.t(
                                                              'MINUTES_TEMPLATES_DILIGENT_TEMPLATE_TITLE'
                                                          )
                                                        : sysTpl.title ===
                                                          MINUTES_TEMPLATES_DILIGENT_ACADEMY_TEMPLATE_TITLE
                                                        ? i18n.t(
                                                              'MINUTES_TEMPLATES_DILIGENT_ACADEMY_TEMPLATE_TITLE'
                                                          )
                                                        : null}
                                                </Typography>
                                                <Typography className={classes.subtitle}>
                                                    {sysTpl?.description ===
                                                    MINUTES_TEMPLATES_DILIGENT_TEMPLATE_DESCRIPTION
                                                        ? i18n.t(
                                                              'MINUTES_TEMPLATES_DILIGENT_TEMPLATE_DESCRIPTION'
                                                          )
                                                        : sysTpl?.description ===
                                                          MINUTES_TEMPLATES_DILIGEN_ACADEMY_TEMPLATE_DESCRIPTION
                                                        ? i18n.t(
                                                              'MINUTES_TEMPLATES_DILIGEN_ACADEMY_TEMPLATE_DESCRIPTION'
                                                          )
                                                        : null}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box className={classes.rightSection}>
                                            {/* TODO: Add conditional download button for system templates */}
                                            <Button
                                                data-analytics="MM-Template-DownloadSystemTemplate"
                                                className={classes.downloadButton}
                                                onClick={(e) => {
                                                    e.preventDefault()
                                                    downloadTemplateFile(sysTpl.id, sysTpl.title)
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === ' ' || e.key === 'Enter') {
                                                        e.preventDefault()
                                                        downloadTemplateFile(
                                                            sysTpl.id,
                                                            sysTpl.title
                                                        )
                                                    }
                                                }}
                                                color="primary"
                                                disableRipple
                                                disableFocusRipple>
                                                <DownloadIcon />
                                                {i18n.t('MINUTES_TEMPLATES_DOWNLOAD_AND_CUSTOMIZE')}
                                            </Button>
                                            {/* Preferred template tag inn list page if isDefault is true */}
                                            <span
                                                className={`${classes.defaultTag} ${
                                                    sysTpl.isDefault === false
                                                        ? classes.defaultTagHidden
                                                        : ''
                                                }`}>
                                                {i18n.t('MINUTES_TEMPLATES_DEFAULT_TEMPLATE')}
                                            </span>

                                            {/* Dropdown options (download and mark preferred) list for system templates */}
                                            <PopoverButton>
                                                {({
                                                    onShowPopover,
                                                    isOpen,
                                                    anchorElement,
                                                    onClosePopover
                                                }) => {
                                                    return (
                                                        <>
                                                            <AtlasTooltip
                                                                title={
                                                                    i18n.t(
                                                                        'TEMPLATE_OPTION_MORE_LABEL'
                                                                    ) as string
                                                                }
                                                                interactive
                                                                placement="bottom">
                                                                <span>
                                                                    <AtlasButton
                                                                        data-testid="MinutesManager_Overflow_Btn"
                                                                        data-analytics="MM-Template-Options"
                                                                        tabIndex={0}
                                                                        className="label-small-link"
                                                                        disableFocusRipple
                                                                        disableRipple
                                                                        onClick={onShowPopover}>
                                                                        <AtlasIcon
                                                                            name="more"
                                                                            size={24}
                                                                        />
                                                                    </AtlasButton>
                                                                </span>
                                                            </AtlasTooltip>
                                                            <Menu
                                                                open={isOpen}
                                                                anchorEl={anchorElement}
                                                                getContentAnchorEl={null}
                                                                anchorOrigin={{
                                                                    vertical: 'bottom',
                                                                    horizontal: 'right'
                                                                }}
                                                                transformOrigin={{
                                                                    vertical: 'top',
                                                                    horizontal: 'right'
                                                                }}
                                                                onClose={onClosePopover}>
                                                                {SYSTEM_TEMPLATE_OPTIONS.map(
                                                                    (option) => {
                                                                        return (
                                                                            <MenuItem
                                                                                key={option.id}
                                                                                style={{
                                                                                    height: '55px'
                                                                                }}
                                                                                className={css`
                                                                                    &:focus {
                                                                                        border: 2px
                                                                                            solid
                                                                                            ${ColorBaseSkyBlue};
                                                                                        border-radius: 2px;
                                                                                    }
                                                                                    &:hover {
                                                                                        background-color: #ebeef2;
                                                                                        border-radius: 2px;
                                                                                    }
                                                                                    ${option.value ===
                                                                                    'DELETE'
                                                                                        ? 'border-top: 1px solid #ccc;'
                                                                                        : ''}
                                                                                `}
                                                                                data-analytics={
                                                                                    option.dataAnalytics
                                                                                }
                                                                                data-testid={
                                                                                    option.testid
                                                                                }
                                                                                onClick={() => {
                                                                                    handleCardOverflowClick(
                                                                                        option.id,
                                                                                        onClosePopover,
                                                                                        sysTpl
                                                                                    )
                                                                                }}>
                                                                                <div
                                                                                    style={{
                                                                                        display:
                                                                                            'flex',
                                                                                        alignItems:
                                                                                            'center',
                                                                                        marginLeft:
                                                                                            '10px',
                                                                                        marginRight:
                                                                                            '20px',
                                                                                        flexDirection:
                                                                                            'row',
                                                                                        justifyContent:
                                                                                            'space-between'
                                                                                    }}>
                                                                                    <div
                                                                                        style={{
                                                                                            paddingRight:
                                                                                                '16px'
                                                                                        }}>
                                                                                        {
                                                                                            option.icon
                                                                                        }
                                                                                    </div>
                                                                                    <div>
                                                                                        {
                                                                                            option.label
                                                                                        }
                                                                                    </div>
                                                                                </div>
                                                                            </MenuItem>
                                                                        )
                                                                    }
                                                                )}
                                                            </Menu>
                                                        </>
                                                    )
                                                }}
                                            </PopoverButton>
                                        </Box>
                                    </Paper>
                                ))}
                        </Box>

                        {(templateLists?.length && templateLists?.length) > 0 ? (
                            <Box>
                                <Typography
                                    variant="h1"
                                    component="h1"
                                    className={classes.allTemplatesText}>
                                    {i18n.t('MINUTES_ALL_TEMPLATES')}
                                </Typography>
                            </Box>
                        ) : null}

                        {templateLists?.length && templateLists?.length > 0 ? (
                            <div
                                className={classes.templateListContainer}
                                style={{ minHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
                                <MinutesTemplatesComponent
                                    templateLists={templateLists}
                                    totalTemplates={totalTemplates}
                                    handlePageChange={handlePageChange}
                                    handleSortBy={handleSortBy}
                                    setDefaultTemplateId={setDefaultTemplateId}
                                    defaultTemplateId={defaultTemplateId}
                                    page={page}
                                    sortBy={sortBy}
                                    reloadTemplateAfterDelete={reloadTemplateAfterDelete}
                                    resetPaginationAndReload={resetPaginationAndReload}
                                    resetPaginationAndReloadWithCurrentSort={
                                        resetPaginationAndReloadWithCurrentSort
                                    }
                                    downloadTemplateFile={downloadTemplateFile}
                                    order={order}
                                    setIsOpen={setIsEditTemplateDrawerOpen}
                                    isOpen={isEditTemplateDrawerOpen}
                                    systemTemplate={systemTemplate}
                                    isLoading={isLoading}
                                    setShowToast={setShowToast}
                                    setToastType={setToastType}
                                    setToastMessage={setToastMessage}
                                />
                            </div>
                        ) : !isLoading ? (
                            <Box className={classes.noDataContainer}>
                                <NoActionIcon />
                                <Typography variant="body1" className={classes.noDataTitle}>
                                    {i18n.t('MINUTES_TEMPLATES_NO_TEMPLATES')}
                                </Typography>
                                <Button
                                    className={classes.buttonsContainer}
                                    disableFocusRipple
                                    data-analytics="MM-Add-Template-No-Templates-Available"
                                    disableTouchRipple
                                    disableRipple
                                    onKeyPress={(e) =>
                                        addKeyPressHandler(e) ? handleAddTemplate : null
                                    }
                                    onClick={handleAddTemplate}>
                                    <Typography
                                        variant="body1"
                                        aria-label={
                                            i18n.t('MINUTES_TEMPLATES_ADD_TEMPLATES') as string
                                        }>
                                        {i18n.t('MINUTES_TEMPLATES_ADD_TEMPLATES')}
                                    </Typography>
                                </Button>
                            </Box>
                        ) : null}
                    </PageBodyTemplate>
                </PageLayout>
                <PreviewTemplateDrawer
                    imageUrlsData={imageUrls}
                    loading={loading}
                    setIsOpen={setIsPreviewDrawerOpen}
                    isOpen={isPreviewDrawerOpen}
                    downloadTemplateFile={downloadTemplateFile}
                    currentTemplate={currentPreviewedTemplate}
                    systemTemplate={systemTemplate}
                />
                <TemplateDrawer
                    setIsOpen={setIsTemplateDrawerOpen}
                    isOpen={isTemplateDrawerOpen}
                    items={items}
                    systemTemplate={systemTemplate}
                    setItems={setItems}
                    reloadTemplateList={() => {}}
                    uploadStatus={uploadStatus}
                    downloadTemplateFile={downloadTemplateFile}
                    setUploadStatus={setUploadStatus}
                    fileUploadError={fileUploadError}
                    setShowToast={setShowToast}
                    setToastType={setToastType}
                    setToastMessage={setToastMessage}
                    setTemplateAdded={setTemplateAdded}
                    onTemplateAdded={handleTemplateAdded}
                />
            </MuiPickersUtilsProvider>
            {toastNotification}
            {/* Writing Style Toast Notifications */}
            <div className="custom-toast-container">
                {Array.from(new Set(toasts.map((toast) => toast.id))).map((id) => {
                    const toast = toasts.find((t) => t.id === id)
                    return toast?.view_toast === true ? (
                        <StatusToast key={toast.id} {...toast} removeToast={removeToast} />
                    ) : null
                })}
            </div>
        </MuiThemeProvider>
    )
}

export type OptionType = 'DEFAULT'
